import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { contactInfo } from '../data';
import ScrollReveal from './ScrollReveal';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  React.useEffect(() => {
    // Check URL params on mount
    const searchParams = new URLSearchParams(window.location.search);
    const msgParam = searchParams.get('message');
    if (msgParam) {
      setFormData(prev => ({ ...prev, message: msgParam }));
    }

    // Listen for custom event
    const handlePrefill = (e) => {
      setFormData(prev => ({ ...prev, message: e.detail }));
    };

    window.addEventListener('prefill-contact', handlePrefill);
    return () => {
      window.removeEventListener('prefill-contact', handlePrefill);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error('Der skete en fejl ved afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Fejl ved formularafsendelse:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Kunne ikke oprette forbindelse. Prøv igen senere.');
    }
  };

  return (
    <section id="kontakt" className="pt-12 pb-0 sm:py-16 bg-stone-100 relative overflow-hidden">
      {/* Decorative background light */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5">
            <ScrollReveal variant="fade-right" duration={800}>
              <div className="flex flex-col justify-between h-full">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2 mb-6 tracking-wider uppercase">
                    Få et uforpligtende tilbud på din have
                  </h2>
                  <p className="text-stone-600 text-base leading-relaxed mb-10">
                    Har du brug for en professionel hånd til haven eller flisearbejde? Udfyld formularen, eller ring direkte til mig. Jeg kommer gerne ud og kigger på din have og giver et uforpligtende tilbud.
                  </p>

                  {/* Contact Details */}
                  <div className="divide-y divide-stone-200">
                    <a
                      href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
                      className="flex items-center gap-4 py-5 transition-all group hover:text-emerald-700"
                    >
                      <div className="text-stone-900 flex-shrink-0 group-hover:text-emerald-700 transition-colors">
                        <Phone className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Ring direkte</p>
                        <p className="text-stone-900 font-bold text-lg mt-0.5">{contactInfo.phone}</p>
                      </div>
                    </a>

                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="flex items-center gap-4 py-5 transition-all group hover:text-emerald-700"
                    >
                      <div className="text-stone-900 flex-shrink-0 group-hover:text-emerald-700 transition-colors">
                        <Mail className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Send en e-mail</p>
                        <p className="text-stone-900 font-bold text-lg mt-0.5">{contactInfo.email}</p>
                      </div>
                    </a>

                    <div className="flex items-center gap-4 py-5">
                      <div className="text-stone-900 flex-shrink-0">
                        <MapPin className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-xs text-stone-500 font-semibold uppercase tracking-wider">Adresse</p>
                        <p className="text-stone-900 font-bold text-base mt-0.5">{contactInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <ScrollReveal variant="fade-left" duration={800}>
              <div className="bg-white border-y sm:border border-stone-200 -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-6 sm:p-10 shadow-2xl shadow-stone-300/70 relative overflow-hidden">
                
                {/* Logo Watermark */}
                <div className="absolute -right-12 -bottom-12 w-80 h-80 pointer-events-none opacity-[0.03] z-0">
                  <img 
                    src="https://assets.auxodev.dk/assets/7ce43b4c-cc9c-4d7d-8d95-aa06e798843e/70512332-bee7-446c-aee4-30f22a593190.png" 
                    alt="Watermark" 
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="relative z-10">
                  {status === 'success' ? (
                    <div className="py-12 text-center">
                      <div className="inline-flex p-4 bg-emerald-50 text-emerald-600 rounded-full mb-6">
                        <CheckCircle className="h-12 w-12" />
                      </div>
                      <h3 className="text-xl font-bold text-stone-900 mb-3 uppercase tracking-wider">Tak for din henvendelse!</h3>
                      <p className="text-stone-600 text-base max-w-md mx-auto mb-8">
                        Jeg har modtaget din besked og vil vende tilbage til dig hurtigst muligt – typisk inden for 24 timer på hverdage.
                      </p>
                      <button
                        onClick={() => setStatus('idle')}
                        className="bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold px-6 py-2.5 rounded-xl transition-colors cursor-pointer"
                      >
                        Send en ny besked
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Honeypot field (STRICT ANTI-SPAM REQUIREMENT) */}
                      <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }}>
                        <label htmlFor="website_url">Efterlad dette felt tomt</label>
                        <input
                          id="website_url"
                          type="text"
                          name="website_url"
                          value={formData.website_url}
                          onChange={handleChange}
                          tabIndex="-1"
                          autoComplete="off"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Name */}
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-stone-700 mb-2">
                            Navn <span className="text-emerald-600">*</span>
                          </label>
                          <input
                            id="name"
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Dit fulde navn"
                            className="w-full bg-stone-50 border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 transition-all outline-none"
                          />
                        </div>

                        {/* Phone */}
                        <div>
                          <label htmlFor="phone" className="block text-sm font-semibold text-stone-700 mb-2">
                            Telefonnummer <span className="text-emerald-600">*</span>
                          </label>
                          <input
                            id="phone"
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="F.eks. 12 34 56 78"
                            className="w-full bg-stone-50 border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 transition-all outline-none"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Email */}
                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-stone-700 mb-2">
                            E-mailadresse <span className="text-emerald-600">*</span>
                          </label>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="din@email.dk"
                            className="w-full bg-stone-50 border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 transition-all outline-none"
                          />
                        </div>

                        {/* Address */}
                        <div>
                          <label htmlFor="address" className="block text-sm font-semibold text-stone-700 mb-2">
                            Adresse / By (hvor opgaven skal løses)
                          </label>
                          <input
                            id="address"
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="F.eks. Klitmøller"
                            className="w-full bg-stone-50 border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 transition-all outline-none"
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-stone-700 mb-2">
                          Beskriv dit projekt <span className="text-emerald-600">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Fortæl lidt om hvad du skal have lavet (f.eks. hækkeklipning, ny fliseterrasse, omlægning af græsplæne osv.)"
                          className="w-full bg-stone-50 border border-stone-300 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 rounded-xl px-4 py-3 text-stone-900 placeholder-stone-400 transition-all outline-none resize-none"
                        />
                      </div>

                      {/* Error Message */}
                      {status === 'error' && (
                        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-sm">
                          <AlertCircle className="h-5 w-5 flex-shrink-0" />
                          <p>{errorMessage}</p>
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full flex items-center justify-center gap-2 text-xs sm:text-base font-bold text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-stone-200 disabled:text-stone-400 disabled:cursor-not-allowed py-2.5 sm:py-4 rounded-full transition-all shadow-lg shadow-emerald-700/10 cursor-pointer"
                      >
                        {status === 'loading' ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Sender besked...
                          </>
                        ) : (
                          <>
                            <Send className="h-5 w-5" />
                            Send besked og få gratis tilbud
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </ScrollReveal>
          </div>

        </div>
      </div>
    </section>
  );
}
